import React from 'react';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Award from 'lucide-react/dist/esm/icons/award.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import { useContent } from '../../../contexts/ContentContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import { useTranslations } from '../../../contexts/TranslationContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';

const AboutValueCommunityTitle = 'AboutValueCommunityTitle';
const AboutValueCommunityDesc = 'AboutValueCommunityDesc';
const AboutValueTransparencyTitle = 'AboutValueTransparencyTitle';
const AboutValueTransparencyDesc = 'AboutValueTransparencyDesc';
const AboutValueResponsibleTitle = 'AboutValueResponsibleTitle';
const AboutValueResponsibleDesc = 'AboutValueResponsibleDesc';

const defaultValues = [
  { title: 'Community First', desc: 'Every dollar raised goes back into programs and initiatives that directly benefit the people we serve.' },
  { title: '100% Transparent', desc: 'From draw results to fund allocation, we believe in complete openness with our supporters and community.' },
  { title: 'Responsible Gaming', desc: 'We promote healthy play habits and provide resources to ensure gaming remains fun and safe for everyone.' },
];

export default function AboutValuesSection({ section }) {
  const { c } = useContent();
  const { editMode } = useEditMode();
  const { t: tr } = useTranslations();

  const valuesTitle = tr('about.valuesTitle') || 'Our Values';

  const translatedDefaults = [
    { title: tr('about.communityTitle') || 'Community First', desc: tr('about.communityDesc') || 'Every dollar raised goes back into programs and initiatives that directly benefit the people we serve.' },
    { title: tr('about.transparencyTitle') || '100% Transparent', desc: tr('about.transparencyDesc') || 'From draw results to fund allocation, we believe in complete openness with our supporters and community.' },
    { title: tr('about.responsibleTitle') || 'Responsible Gaming', desc: tr('about.responsibleDesc') || 'We promote healthy play habits and provide resources to ensure gaming remains fun and safe for everyone.' },
  ];

  const values = [
    { icon: Heart, titleKey: AboutValueCommunityTitle, descKey: AboutValueCommunityDesc, defaults: translatedDefaults[0] },
    { icon: Award, titleKey: AboutValueTransparencyTitle, descKey: AboutValueTransparencyDesc, defaults: translatedDefaults[1] },
    { icon: Users, titleKey: AboutValueResponsibleTitle, descKey: AboutValueResponsibleDesc, defaults: translatedDefaults[2] },
  ];

  return (
    <div className="mb-8 sm:mb-16 md:mb-20">
      <h2 className="text-xl sm:text-3xl font-bold text-center text-gray-900 dark:text-white mb-6 sm:mb-12">{valuesTitle}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 sm:gap-8">
        {values.map((value, index) => {
          const Icon = value.icon;
          const title = c(value.titleKey, '') || value.defaults.title;
          const desc = c(value.descKey, '') || value.defaults.desc;
          return (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-8 shadow-md sm:shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div
                className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full mb-3 sm:mb-4"
                style={{ background: 'linear-gradient(135deg, var(--charity-primary, #1e40af), var(--charity-accent, #06b6d4))' }}
              >
                <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <h3 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white mb-1.5 sm:mb-3">
                <InlineTranslationEdit translationKey={value.titleKey} value={title} />
              </h3>
              <p className="text-xs sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                <InlineTranslationEdit translationKey={value.descKey} value={desc} multiline />
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
